/**
 * Static application constants (app info, API config, storage keys, limits, timings).
 */

import { resolveBuildInfo } from '../version/build-info'

// `import.meta.env` is a Vite-only construct: it is `undefined` when this module
// is loaded from a plain Node/tsx context (the SSG prerender in
// scripts/prerender-public-pages.ts runs after `vite build`), where touching
// `import.meta.env.X` throws and aborts the build → the deploy. Read it ONCE,
// defensively, so every consumer below is safe in BOTH the Vite browser build
// (Vite injects the real values) and Node (falls back to the literals below).
const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}

// Static constants (routes, API config, app info) remain hardcoded
const AUTH_ROUTES = {
  // API endpoints
  API: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REGISTER: '/api/auth/register',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    VERIFY_EMAIL: '/api/auth/verify-email',
    REFRESH: '/api/auth/refresh',
  },
  // View routes (frontend pages)
  VIEWS: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
  },
} as const

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PASSWORD_MIN_LENGTH = 8
const FILE_LIMITS = { MAX_FILE_SIZE: 5000000 } // 5MB
const VALIDATION_LIMITS = { GENERAL: {} }

// Message constants removed - backend provides i18n messages directly

// Note: AUTH_ROUTES already defined above - removed duplicate

// Application Info
export const APP_NAME = 'Block&Taxes'

// Deployed build metadata — injected at `vite build` time via the
// `import.meta.env.VITE_GIT_*` keys defined in `vite.config.ts` and read here
// through the same `viteEnv` guard as every other build-time value (safe in
// the Node/tsx prerender, where `import.meta.env` is undefined). Powers the
// always-visible deployed-version indicator. Fallback sentinels (dev/local/
// 0.1.0) apply when no git metadata was injected (dev server / no-git build).
export const BUILD_INFO = resolveBuildInfo(viteEnv)

// API Configuration
//
// Default is the EMPTY STRING = same-origin (relative `/api/...` URLs), which is
// what production serves (nginx proxies /api/* on the SPA's own origin) and what
// the dev server serves (its /api proxy forwards to the backend named by
// VITE_DEV_API_PROXY_TARGET — see src/platform/config/dev-api-proxy-target.ts).
//
// It used to default to 'http://localhost:3000'. On the deploy host :3000 is
// PRODUCTION, so an unset VITE_API_URL sent the browser DIRECTLY at the
// production API, bypassing the dev proxy entirely. Same-origin is the only
// default that cannot name the wrong host: it is the origin the user loaded.
// Set VITE_API_URL to an absolute origin only when the API really is elsewhere.
export const API_BASE_URL = viteEnv.VITE_API_URL ?? ''

export const API_TIMEOUT = 30000 // 30 seconds

// Brand Configuration - centralized domain/email constants
// Single source of truth for product domain, marketing URLs, and contact addresses.
// Override at build time via VITE_BRAND_* environment variables for white-label deploys.
export const BRAND = {
  DOMAIN: viteEnv.VITE_BRAND_DOMAIN ?? 'blockandtaxes.com',
  SITE_URL: viteEnv.VITE_BRAND_SITE_URL ?? 'https://blockandtaxes.com',
  APP_URL: viteEnv.VITE_BRAND_APP_URL ?? 'https://app.blockandtaxes.com',
  DOCS_URL: viteEnv.VITE_BRAND_DOCS_URL ?? 'https://docs.blockandtaxes.com',
  SUPPORT_EMAIL: viteEnv.VITE_BRAND_SUPPORT_EMAIL ?? 'support@blockandtaxes.com',
  NOREPLY_EMAIL: viteEnv.VITE_BRAND_NOREPLY_EMAIL ?? 'noreply@blockandtaxes.com',
} as const

// Legal URLs - terms of service and privacy policy
// Override at build time via VITE_TERMS_URL / VITE_PRIVACY_URL.
export const LEGAL_URLS = {
  TERMS: viteEnv.VITE_TERMS_URL ?? `${BRAND.SITE_URL}/terms`,
  PRIVACY: viteEnv.VITE_PRIVACY_URL ?? `${BRAND.SITE_URL}/privacy`,
} as const

// Local Storage Keys
// Centralized source of truth for ALL localStorage / sessionStorage keys used
// by the frontend. Adding/changing a key here requires updating every consumer
// that references the same physical key — never inline literals in features.
//
// IMPORTANT — STORAGE SERVICE PREFIX:
// The shared `storageService` (packages/frontend/src/shared/services/storage.service.ts)
// AUTO-PREFIXES every key passed to its `setLocal/getLocal/removeLocal` methods
// with `'afcripto_'`. So a value here like `AUTH_TOKEN: 'afcripto_auth_token'`
// ends up physically stored as `'afcripto_afcripto_auth_token'` in localStorage.
// The values below are the keys passed TO `storageService` — NOT the physical
// localStorage keys. Code that bypasses `storageService` (raw `localStorage.*`
// calls) is BROKEN and must be migrated to `storageService.*` helpers.
// For unavoidable raw access, see `STORAGE_PHYSICAL_KEYS` below.
//
// NOTE on THEME: kept as `'theme'` (not `'afcripto_theme'`) because that is
// the value the global `useTheme` hook has always written. Changing the value
// would silently drop the user's saved preference on first load.
//
// NOTE on THEME vs THEME_PREFERENCE: two distinct semantics, kept in sync.
//  - THEME (`'theme'`): the RESOLVED theme value read on boot by the `useTheme`
//    hook (and the transaction-matching page) — only ever `'light'` or `'dark'`.
//  - THEME_PREFERENCE (`'bat-theme-preference'`): the MODE preference written by
//    the `ThemeProvider` context — can also be `'system'` (follow OS).
//  They cannot be merged (only THEME_PREFERENCE can express "follow system"),
//  but they must NOT diverge: ThemeProvider mirrors every resolved theme it
//  paints into THEME (see shared/theme/ThemeProvider.tsx `applyThemeToDOM`), so
//  a change made via the navbar toggle / Preferences dropdown survives a reload
//  regardless of which system wins the boot race.
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'afcripto_auth_token',
  REFRESH_TOKEN: 'afcripto_refresh_token',
  USER_DATA: 'afcripto_user_data',
  USER_PREFERENCES: 'afcripto_user_preferences',
  USER_CONFIG: 'afcripto_user_config',
  THEME: 'theme',
  THEME_PREFERENCE: 'bat-theme-preference',
  LANGUAGE: 'afcripto_language',
  LAST_ROUTE: 'afcripto_last_route',
  TABLE_SETTINGS: 'afcripto_table_settings',
  BALANCE_LAST_VERIFIED_AT: 'balance-last-verified-at',
  ORG_JOBS_AUTO_REFRESH: 'org-jobs-auto-refresh',
  AUDIO_FEEDBACK: 'audioFeedbackSettings',
  // Impersonation session keys — written/read via sessionStorage by
  // ImpersonationContext and ImpersonateUserModal. NOT prefixed by
  // storageService because they go through raw sessionStorage directly.
  IMPERSONATION_TOKEN: 'impersonation_token',
  IMPERSONATION_USER: 'impersonation_user',
  IMPERSONATION_ADMIN_ID: 'impersonation_admin_id',
  IMPERSONATION_EXPIRES_AT: 'impersonation_expires_at',
  // Hand-off slot. The admin tab writes the freshly minted session HERE, never
  // into the four keys above: `window.open` gives the new tab a COPY of
  // sessionStorage, so writing the ACTIVE keys would also turn the admin's own
  // tab into an impersonated session. The destination tab promotes the hand-off
  // into the active keys and deletes it; the opener deletes its own copy.
  IMPERSONATION_HANDOFF: 'impersonation_handoff',
  // One-shot flag: the impersonated session hit its expiry. Survives the reload
  // so the admin is TOLD they were returned to their own session.
  IMPERSONATION_EXPIRED_NOTICE: 'impersonation_expired_notice',
} as const

// Shared page-size presets for table/list pagination across features.
// Pick the bucket by table density / volume:
//   SMALL  → tight modal tables, narrow side panels
//   MEDIUM → admin tables, reports, dashboards
//   LARGE  → data-heavy tables (lots, attributions, bulk views)
//   XL     → API-max / "fetch all and paginate client-side" pattern
//            (backend Zod schemas commonly cap pageSize at 100; use this
//             constant when the intent is "one request, all visible rows").
// Use these constants instead of hardcoding numeric pageSize values
// so the defaults can be tuned in one place.
//
// NOTE: page sizes that don't match these buckets (e.g. 9 for a 3x3 grid,
// 25 inherited from user-config defaultPageSize, 200 for "fetch all" search
// boxes) stay as literals with an inline comment — they encode a specific
// UI/UX or backend constraint, not a generic density preference.
export const PAGINATION_DEFAULTS = {
  SMALL: 10,
  MEDIUM: 20,
  LARGE: 50,
  XL: 100,
} as const

// Validation Rules - use shared constants
export const VALIDATION = {
  EMAIL_REGEX,
  PASSWORD_MIN_LENGTH,
  MAX_FILE_SIZE: FILE_LIMITS.MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES: ['.csv', '.xlsx', '.json'], // Keep local for now as it's app-specific
  ...VALIDATION_LIMITS.GENERAL,
} as const

// Tax Validation Limits — upper sanity bounds for tax-config form inputs.
//
// Per-country limits now live in `bat.tax_configurations` and are surfaced to
// the UI via `useEffectiveTaxConfig()` (Phase 4b of Issue #358). These
// constants are kept ONLY as upper sanity caps that bound ANY country's
// resolved value — they catch insane inputs and keep the form usable while
// the per-country config is still loading. Components should clamp via:
//   max = min(perCountryLimit, TAX_VALIDATION_LIMITS.MAX_*)
export const TAX_VALIDATION_LIMITS = {
  MIN_CARRYFORWARD_YEARS: 1,
  MAX_CARRYFORWARD_YEARS: 10,
  MIN_HOLDING_DAYS: 1,
  MAX_HOLDING_DAYS: 3650,
  MIN_WASH_SALE_DAYS: 1,
  MAX_WASH_SALE_DAYS: 365,
  MIN_PERCENTAGE: 0,
  MAX_PERCENTAGE: 100,
} as const

// UI Timings (milliseconds) — durations for setTimeout-based UX feedback.
// Centralized so the perceived "snappiness" of the app can be tuned in one
// place. Each entry encodes a deliberate UX choice, not an arbitrary number:
//   COPY_FEEDBACK   — how long the "Copied!" indicator stays visible after
//                     a navigator.clipboard.writeText. 2s is long enough to
//                     be noticed but short enough not to feel sticky.
//   TOAST_SUCCESS   — default react-hot-toast duration for successful ops.
//   TOAST_INFO      — auto-dismiss budget of an info notification.
//   TOAST_WARNING   — auto-dismiss budget of a warning (as long as an error:
//                     both ask the user to read and decide).
//   TOAST_ERROR     — react-hot-toast duration for errors (kept LONGER on
//                     purpose so the user can read the failure reason).
//                     DO NOT collapse TOAST_SUCCESS and TOAST_ERROR — the
//                     gap is semantic.
//   SUCCESS_MESSAGE — inline "Saved!" / "Updated!" banners on settings
//                     forms, cleared by setTimeout after this many ms.
//   I18N_RETRY      — backoff for I18nWrapper while waiting for i18next to
//                     finish loading translation resources.
export const UI_TIMINGS = {
  COPY_FEEDBACK: 2000,
  TOAST_SUCCESS: 4000,
  TOAST_INFO: 4000,
  TOAST_WARNING: 6000,
  TOAST_ERROR: 6000,
  SUCCESS_MESSAGE: 3000,
  I18N_RETRY: 100,
} as const

// Responsive breakpoints (pixels) — used by JS-side viewport checks (resize
// listeners, chart sizing). Keep these in sync with Tailwind's `sm` (640px)
// and `lg` (1024px) defaults — they are the values that already determine
// when CSS layout flips between mobile / tablet / desktop columns.
//
// Use these constants for `window.innerWidth < BREAKPOINTS.TABLET` style
// media queries in TS, NOT in CSS (Tailwind utility classes handle CSS).
export const BREAKPOINTS = {
  MOBILE: 640,
  TABLET: 1024,
  DESKTOP: 1280,
  WIDE: 1920,
} as const

// Neutral gray fallbacks for missing brand/category colors. Single source
// of truth — do NOT inline `#6b7280` / `#6e7681` in feature components.
//   NEUTRAL — Tailwind's `gray-500`. Default for missing asset/category
//             color and missing exchange brandColor. The canonical value.
export const COLOR_FALLBACKS = {
  NEUTRAL: '#6b7280',
} as const

// Error and Success Messages removed - backend provides i18n messages directly

// Authentication Configuration
export const AUTH_CONFIG = {
  TOKEN_EXPIRY: 3600, // 1 hour in seconds
  REFRESH_TOKEN_EXPIRY: 2592000, // 30 days in seconds
  TOKEN_REFRESH_THRESHOLD: 300, // 5 minutes in seconds
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 300, // 5 minutes in seconds
  // Routes imported from @bat/shared
  ROUTES: {
    LOGIN: AUTH_ROUTES.API.LOGIN,
    REGISTER: AUTH_ROUTES.API.REGISTER,
    LOGOUT: AUTH_ROUTES.API.LOGOUT,
    REFRESH: AUTH_ROUTES.API.REFRESH,
    PROFILE: '/api/auth/me', // Profile route not defined in AUTH_ROUTES
    CHANGE_PASSWORD: '/api/auth/change-password', // Change password route not defined in AUTH_ROUTES
  },
} as const
